import { useMemo, useRef, useEffect, useState } from 'react';

import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';

/**
 * Hook centralizado para gerenciar o estado de inicialização da aplicação
 * Evita race conditions entre diferentes contextos durante o carregamento
 */
export const useAppInitialization = () => {
  const { token, user, isLoading: userLoading } = useUser();
  const { menu, isLoading: menuLoading } = useMenu();
  const { isInitialized: menuSelectionInitialized } = useMenuSelection();

  // Estado para controlar se já passou pela primeira inicialização
  const [hasInitialized, setHasInitialized] = useState(false);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Estado unificado de inicialização
  const isFullyInitialized = useMemo(() => {
    const hasToken = !!token;
    const hasUser = !!user;
    const hasMenu = menu.length > 0;
    const isUserReady = !userLoading;
    const isMenuReady = !menuLoading;
    const isMenuSelectionReady = menuSelectionInitialized;

    const fullyInitialized =
      hasToken && hasUser && hasMenu && isUserReady && isMenuReady && isMenuSelectionReady;

    console.log('[useAppInitialization] Estado de inicialização:', {
      hasToken,
      hasUser,
      hasMenu,
      isUserReady,
      isMenuReady,
      isMenuSelectionReady,
      fullyInitialized,
      hasInitialized,
      menuLength: menu.length,
      currentPath: window.location.pathname,
    });

    return fullyInitialized;
  }, [
    token,
    user,
    userLoading,
    menu.length,
    menuLoading,
    menuSelectionInitialized,
    hasInitialized,
  ]);

  // Efeito para marcar quando a inicialização foi concluída
  useEffect(() => {
    console.log('[useAppInitialization] Verificando inicialização:', {
      isFullyInitialized,
      hasInitialized,
      token: !!token,
      user: !!user,
      userLoading,
      menuLength: menu.length,
      menuLoading,
      menuSelectionInitialized,
    });

    if (isFullyInitialized && !hasInitialized) {
      console.log('[useAppInitialization] ✅ Inicialização completa detectada!');
      setHasInitialized(true);
    }
  }, [
    isFullyInitialized,
    hasInitialized,
    token,
    user,
    userLoading,
    menu.length,
    menuLoading,
    menuSelectionInitialized,
  ]);

  // Timeout de segurança para evitar travamento
  useEffect(() => {
    if (!hasInitialized) {
      initializationTimeoutRef.current = setTimeout(() => {
        console.warn(
          '[useAppInitialization] ⚠️ Timeout de inicialização atingido, forçando inicialização'
        );
        setHasInitialized(true);
      }, 10000); // 10 segundos de timeout
    }

    return () => {
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [hasInitialized]);

  // Estado de loading detalhado para debug
  const loadingStates = useMemo(
    () => ({
      userLoading,
      menuLoading,
      menuSelectionInitialized,
      hasToken: !!token,
      hasUser: !!user,
      hasMenu: menu.length > 0,
      // Estados adicionais para melhor controle
      isUserContextReady: !userLoading && !!token && !!user,
      isMenuContextReady: !menuLoading && menu.length > 0,
      isMenuSelectionReady: menuSelectionInitialized,
    }),
    [userLoading, menuLoading, menuSelectionInitialized, token, user, menu.length]
  );

  return {
    isFullyInitialized,
    hasInitialized,
    loadingStates,
    // Estados individuais para casos específicos
    isUserReady: !userLoading && !!token && !!user,
    isMenuReady: !menuLoading && menu.length > 0,
    isMenuSelectionReady: menuSelectionInitialized,
  };
};
